import moment from 'moment';
import { sendSingleRequest } from '../apis';
import {
  IFarmResource,
  ILineResource,
  IOysterLineResource,
  ISpatStorageResource,
} from '../entities/farms.entities';
import { IAccountSetting } from '../entities/user.entities';
import { ICropSpecie } from '../entities/utils.entities';
import { IBasicInventory } from '../entities/inventory.entities';
import {
  pcsToDoz,
  defaultDateFormat,
  displayValueUnit,
} from './common.helpers';
import {
  IMusselGrowingCycle,
  IOysterGrowingCycle,
  IOysterHarvest,
} from '../entities/growing.entities';
import { formatNumber } from '../entities/util-functions';
import { TBusinessType } from '../entities/general.entities';
import { LINE_COLORS } from './constant';
import { ICustomValue } from '../entities/automation.entities';

export const updateLineColumn = async (line_id: number, col_no: number) => {
  const response = await sendSingleRequest(
    { line_id, col_no },
    'POST',
    `api/farm/line/visual-line/column`,
    true,
  );
  if (response.status) {
    return response.data;
  } else {
    throw new Error(response.data?.message ?? 'Something went wrong');
  }
};

export const updateLinePriority = async (line_id: number, priority: number) => {
  const response = await sendSingleRequest(
    { line_id, priority },
    'POST',
    `api/farm/line/visual-line/priority`,
    true,
  );
  if (response.status) {
    return response.data;
  } else {
    throw new Error(response.data?.message ?? 'Something went wrong');
  }
};

type LineStatus =
  | 'EMPTY'
  | 'READY_HARVEST'
  | 'REQUIRE_ASSESSMENT'
  | 'CATCH_SPAT'
  | 'NORMAL';

export const getLineStatus = (
  line: ILineResource,
  expire_days = 90,
): LineStatus => {
  const ready_harvest = line.growing_cycle?.ready_harvest;
  const requireAssessment = () => {
    if (!line || !line.growing_cycle) return false;
    const lastDate =
      line.growing_cycle.last_assessment?.assessment_date ??
      line.growing_cycle.main_seed.planned_date_seed;
    const d1 = moment(Number(lastDate) * 1000)
      .toDate()
      .getTime();
    const d2 = moment().toDate().getTime();
    const d = Math.abs(Math.round((d2 - d1) / 86400000));

    return d > (expire_days ?? 90);
  };
  const catchSpat = !!line.growing_cycle?.main_seed.is_catch_spat;

  if (!line.growing_cycle) return 'EMPTY';
  else if (ready_harvest) return 'READY_HARVEST';
  else if (requireAssessment()) return 'REQUIRE_ASSESSMENT';
  else if (catchSpat) return 'CATCH_SPAT';
  else return 'NORMAL';
};

export const getLineColor = (
  line: ILineResource,
  labels: IAccountSetting['stage_labels'],
  expire_days = 90,
) => {
  if (line.growing_cycle?.main_seed.stage) {
    const stage = line.growing_cycle.main_seed.stage;
    if (labels && labels.length >= stage && labels[stage - 1].color) {
      return labels[stage - 1].color!;
    }
  }
  const status = getLineStatus(line, expire_days);

  return LINE_COLORS[status];
};

export const setFarmVisualFloatsPosition = async (
  seeding_id: number,
  inventory_id: number,
  positions: number[],
  shouldQueue: boolean = false,
) => {
  setLocalFloatsPosition(seeding_id, inventory_id, positions);

  if (shouldQueue) {
    if (!!(window as any).VisualFloatsPositionTimer) {
      clearTimeout((window as any).VisualFloatsPositionTimer);
    }
    if (!(window as any).VisualFloatsPositionQueue) {
      (window as any).VisualFloatsPositionQueue = [];
    }
    (window as any).VisualFloatsPositionQueue.push({
      seeding_id,
      inventory_id,
      positions,
    });
    (window as any).VisualFloatsPositionTimer = setTimeout(async () => {
      if (
        !!(window as any).VisualFloatsPositionQueue &&
        (window as any).VisualFloatsPositionQueue.length > 0
      ) {
        await sendSingleRequest(
          { bulk_data: (window as any).VisualFloatsPositionQueue },
          'POST',
          `api/farm/line/visual-floats-position`,
          true,
        );
        (window as any).VisualFloatsPositionQueue = null;
      }
    }, 3000);
  } else {
    const response = await sendSingleRequest(
      { seeding_id, inventory_id, positions },
      'POST',
      `api/farm/line/visual-floats-position`,
      true,
    );
    return response.status;
  }
};

export const getLocalFloatsPosition = (
  seeding_id: number,
  inventory_id: number,
) => {
  const res = localStorage.getItem(
    `farm_visual_floats_position_${seeding_id}_${inventory_id}`,
  );
  if (res) return JSON.parse(res);
  return null;
};

export const setLocalFloatsPosition = (
  seeding_id: number,
  inventory_id: number,
  positions: number[],
) => {
  localStorage.setItem(
    `farm_visual_floats_position_${seeding_id}_${inventory_id}`,
    JSON.stringify(positions),
  );
};

export const calcOnGrowWastePercent = (
  onGrowWaste: IAccountSetting['oyster_crops'],
  type: 'grow' | 'waste',
  lineID: number | undefined = undefined,
) => {
  if (!lineID) {
    return (
      (type === 'grow' ? onGrowWaste?.grow_ons : onGrowWaste?.waste) ??
      undefined
    );
  } else {
    return (
      (type === 'grow'
        ? onGrowWaste?.lines_grow_ons &&
          onGrowWaste.lines_grow_ons[lineID] !== undefined &&
          onGrowWaste.lines_grow_ons[lineID] !== null
          ? onGrowWaste.lines_grow_ons[lineID]
          : onGrowWaste?.grow_ons
        : onGrowWaste?.lines_waste &&
            onGrowWaste.lines_waste[lineID] !== undefined &&
            onGrowWaste.lines_waste[lineID] !== null
          ? onGrowWaste.lines_waste[lineID]
          : onGrowWaste?.waste) ?? undefined
    );
  }
};

export const calcOysterPercent = (
  specie: ICropSpecie,
  lineID: number | undefined,
) => {
  if (!lineID) {
    return specie.probability ?? undefined;
  } else {
    return specie.lines_props && specie.lines_props[lineID] !== undefined
      ? specie.lines_props[lineID]
      : (specie.probability ?? undefined);
  }
};

export const getEstHarvestDate = (line: ILineResource) =>
  line.growing_cycle?.last_assessment?.planned_date_harvest ??
  line.growing_cycle?.main_seed.planned_date_harvest;

export const calcLineLastSize = (line: ILineResource) => {
  if (!line.growing_cycle) return undefined;
  if (line.growing_cycle.last_assessment) {
    return line.growing_cycle.last_assessment.shell_size;
  }
  const min = line.growing_cycle.main_seed.spat_size;
  const max = line.growing_cycle.main_seed.spat_size_max ?? min;
  const avg = (min + max) / 2;
  return { min, max, avg };
};

export const getLineOysters = (
  line: IOysterLineResource,
  unit: 'dozens' | 'pieces' = 'dozens',
) => {
  if (!line.growing_cycle) return undefined;
  if (
    line.growing_cycle.last_assessment &&
    line.growing_cycle.last_assessment.estimated_amount
  ) {
    return unit === 'dozens'
      ? pcsToDoz(line.growing_cycle.last_assessment.estimated_amount)
      : line.growing_cycle.last_assessment.estimated_amount;
  }
  return unit === 'dozens'
    ? line.growing_cycle.current_amount_dz
    : line.growing_cycle.current_amount_pcs;
};

export const calcSpecieLineDozens = (
  line: IOysterLineResource,
  onGrowWaste: IAccountSetting['oyster_crops'],
  oysterSpecies: ICropSpecie[],
) => {
  if (!line.growing_cycle) return undefined;
  const totAmount = getLineOysters(line) ?? 0;
  const growPercent = calcOnGrowWastePercent(onGrowWaste, 'grow', line.id);
  const wastePercent = calcOnGrowWastePercent(onGrowWaste, 'waste', line.id);
  let result = [
    {
      name: 'Grown ons',
      amount:
        growPercent === undefined ? undefined : (totAmount * growPercent) / 100,
    },
    {
      name: 'Waste',
      amount:
        wastePercent === undefined
          ? undefined
          : (totAmount * wastePercent) / 100,
    },
  ];
  for (const field of oysterSpecies) {
    const percent = calcOysterPercent(field, line.id);
    result.push({
      name: field.name,
      amount: percent === undefined ? undefined : (totAmount * percent) / 100,
    });
  }
  return result;
};

export const calcOysterLineSize = (
  line: IOysterLineResource,
  onGrowWaste: IAccountSetting['oyster_crops'],
  oysterSpecies: ICropSpecie[],
) => {
  if (!line.growing_cycle) return undefined;
  if (line.growing_cycle.last_assessment) {
    return line.growing_cycle.last_assessment.shell_size;
  }
  let minArr: Array<{ size: number; amount: number }> = [];
  if (onGrowWaste?.grow_ons_json?.size_min) {
    const amount = calcOnGrowWastePercent(onGrowWaste, 'grow', line.id);
    if (amount !== undefined) {
      minArr.push({
        size: onGrowWaste.grow_ons_json.size_min,
        amount: amount,
      });
    }
  }
  if (onGrowWaste?.waste_json?.size_min) {
    const amount = calcOnGrowWastePercent(onGrowWaste, 'waste', line.id);
    if (amount !== undefined) {
      minArr.push({
        size: onGrowWaste.waste_json.size_min,
        amount: amount,
      });
    }
  }
  for (const specie of oysterSpecies) {
    if (specie.size_min !== null) {
      const amount = calcOysterPercent(specie, line.id);
      if (amount !== undefined) {
        minArr.push({
          size: specie.size_min,
          amount: amount,
        });
      }
    }
  }
  let maxArr: Array<{ size: number; amount: number }> = [];
  if (onGrowWaste?.grow_ons_json?.size_max) {
    const amount = calcOnGrowWastePercent(onGrowWaste, 'grow', line.id);
    if (amount !== undefined) {
      maxArr.push({
        size: onGrowWaste.grow_ons_json.size_max,
        amount: amount,
      });
    }
  }
  if (onGrowWaste?.waste_json?.size_max) {
    const amount = calcOnGrowWastePercent(onGrowWaste, 'waste', line.id);
    if (amount !== undefined) {
      maxArr.push({
        size: onGrowWaste.waste_json.size_max,
        amount: amount,
      });
    }
  }
  for (const crop of oysterSpecies) {
    if (crop.size_max !== null) {
      const amount = calcOysterPercent(crop, line.id);
      if (amount !== undefined) {
        maxArr.push({
          size: crop.size_max,
          amount: amount,
        });
      }
    }
  }
  const minSum = minArr.reduce((acc, x) => acc + x.amount, 0);
  if (minSum <= 0) {
    return undefined;
  }
  const min = minArr.reduce((acc, x) => acc + (x.size * x.amount) / minSum, 0);
  const maxSum = maxArr.reduce((acc, x) => acc + x.amount, 0);
  if (maxSum <= 0) {
    return { min, avg: min, max: min };
  }
  const max = maxArr.reduce((acc, x) => acc + (x.size * x.amount) / maxSum, 0);

  return { min, avg: (min + max) / 2, max };
};

const makeSpatLabel = (spat: ISpatStorageResource) => {
  let res: string[] = [];
  if (spat.source_line) {
    res.push(`${spat.source_line.farm.name}/${spat.source_line.line_name}`);
  }
  if (spat.seed_type) res.push(spat.seed_type);
  res.push(defaultDateFormat(spat.collection_date) ?? '-');
  res.push(spat.source);
  return res.join(', ');
};

export const getSpatSourceLabel = (
  spatID: number,
  spats: ISpatStorageResource[],
) => {
  const spat = spats.find(x => x.id === spatID);
  return spat ? makeSpatLabel(spat) : undefined;
};

export const inventoryLabel = (ivt: IBasicInventory | undefined) =>
  ivt
    ? ['FLOAT', 'TIES', 'LONGLINE'].includes(ivt.type)
      ? `${ivt.type} ${ivt.name}`
      : ivt.type === 'OYSTER'
        ? ivt.name
        : ivt.name
    : '';

export const getMortalityLabel = (harvest: IOysterHarvest, unit = 'dz') =>
  harvest.amount_pcs && harvest.mortality
    ? `${formatNumber(pcsToDoz(harvest.mortality))} ${unit} (${formatNumber(
        (harvest.mortality / harvest.amount_pcs) * 100,
      )} %)`
    : '';

export const extractSeedingForm = (
  farm: IFarmResource | undefined,
  overForm?: any,
) => {
  if (!farm) return {};
  const seedingForm: any = farm.default_seeding_form;

  let result: any = {};
  if (seedingForm) {
    for (let key in seedingForm) {
      if (seedingForm[key] !== undefined || seedingForm[key] !== null) {
        result[key] = seedingForm[key];
      }
    }
  }
  if (overForm) {
    for (let key in overForm) {
      if (
        overForm[key] !== undefined &&
        overForm[key] !== null &&
        key in result
      ) {
        result[key] = overForm[key];
      }
    }
  }
  return result;
};

interface ILineGrowing {
  id: number;
  line_name: string;
  is_growing: boolean;
  stage?: number;
  planned_date_seed?: number;
  planned_date_harvest?: number;
  shell_size_min?: number | null;
  shell_size_max?: number | null;
  shell_size_avg?: number | null;
  density?: number | null;
  density_max?: number | null;
  density_unit?: string | null;
  estimated_amount?: number | null;
  assessment_custom_values?: ICustomValue[];
}

export const getLineGrowingData = (line: ILineResource) => {
  let result: ILineGrowing = {
    id: line.id,
    line_name: line.line_name,
    is_growing: false,
  };
  const cycle = line.growing_cycle;
  if (!cycle) {
    return result;
  }
  result = {
    ...result,
    is_growing: true,
    stage: cycle.main_seed.stage,
    planned_date_seed: cycle.main_seed.planned_date_seed,
    planned_date_harvest:
      cycle.last_assessment?.planned_date_harvest ??
      cycle.main_seed.planned_date_harvest,
    shell_size_min:
      cycle.last_assessment?.shell_size?.min ?? cycle.main_seed.spat_size,
    shell_size_max:
      cycle.last_assessment?.shell_size?.max ?? cycle.main_seed.spat_size_max,
    shell_size_avg:
      cycle.last_assessment?.shell_size?.avg ??
      (cycle.main_seed.spat_size +
        (cycle.main_seed.spat_size_max ?? cycle.main_seed.spat_size)) /
        2,
    estimated_amount: cycle.last_assessment?.estimated_amount,
    density: cycle.last_assessment?.density,
    density_max: cycle.last_assessment?.density_max,
  };
  if (line.crop_type === 'MUSSEL') {
    const musselCycle = cycle as IMusselGrowingCycle;
    result = {
      ...result,
      density: result.density ? result.density : musselCycle.main_seed.density,
      density_max:
        result.density || result.density_max
          ? result.density_max
          : musselCycle.main_seed.density_max,
      density_unit: musselCycle.main_seed.density_unit,
    };
  } else if (line.crop_type === 'OYSTER') {
    const oysterCycle = cycle as IOysterGrowingCycle;
    result = {
      ...result,
      density: result.density ? result.density : oysterCycle.main_seed.density,
      density_unit: `${oysterCycle.main_seed.amount_unit} per ${
        (line as IOysterLineResource).farming_method
      }`,
    };
  }
  if (cycle.last_assessment?.custom_values) {
    result = {
      ...result,
      assessment_custom_values: cycle.last_assessment.custom_values,
    };
  }
  return result;
};

export const getMostCommonCropType = (
  farms: IFarmResource[],
): TBusinessType | undefined => {
  const allLines = farms.flatMap(farm => farm.lines);
  if (allLines.length === 0) {
    return undefined;
  }
  const counts = allLines.reduce(
    (acc, line) => {
      const cropType = line.crop_type;
      acc[cropType] = (acc[cropType] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const mostCommon = Object.entries(counts).reduce((a, b) =>
    a[1] > b[1] ? a : b,
  )[0] as TBusinessType;

  return mostCommon;
};

export const weightAmountLabel = (
  cropType: TBusinessType,
  unitType: IAccountSetting['display_unit'],
  amount: number | null | undefined,
  weightUnit: 'weight_kg' | 'weight_t' | null = null,
  emptyChar: string = '',
) => {
  if (amount === null || amount === undefined) {
    return emptyChar;
  }
  if (cropType === 'MUSSEL') {
    return displayValueUnit(
      amount,
      unitType,
      weightUnit ?? 'weight_kg',
      emptyChar,
    );
  }
  if (cropType === 'OYSTER') {
    return `${pcsToDoz(amount)} dozens`;
  }
  if (cropType === 'SEAWEED') {
    return displayValueUnit(
      amount,
      unitType,
      weightUnit ?? 'weight_kg',
      emptyChar,
    );
  }
  return amount.toString();
};

export const lineLengthLabel = (
  cropType: TBusinessType,
  lineType: ILineResource['line_type'],
  farmingMethod: string | null | undefined,
  unitType: IAccountSetting['display_unit'],
  length: number | null | undefined,
  emptyChar: string = '',
) => {
  if (length === null || length === undefined) {
    return emptyChar;
  }
  if (cropType === 'MUSSEL') {
    if (lineType === 'LONGLINE') {
      return displayValueUnit(length, unitType, 'distance_m', emptyChar);
    } else {
      return `${length} droppers`;
    }
  }
  if (cropType === 'OYSTER') {
    return `${length} ${farmingMethod ?? 'baskets'}`;
  }
  if (cropType === 'SEAWEED') {
    return displayValueUnit(length, unitType, 'area_sqm', emptyChar);
  }
  return length.toString();
};
